import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
  useContext,
  // useRef, // No longer needed here, ShepherdRunner handles its own ref
} from "react"; // Added useEffect
// import Shepherd from 'shepherd.js'; // Removed, ShepherdRunner handles direct Shepherd import
import ShepherdRunner from '../components/tutorial/ShepherdRunner'; // Corrected Import ShepherdRunner
import { initializeTutorial, getTourOptions } from "./ShepherdConfig";
// Firebase auth import for UID
import { auth } from "../firebase/firebase"; // Adjust path as necessary
import {
  saveUserTutorialState,
  loadUserTutorialState,
} from "../firebase/tutorialService"; // Import Firestore functions
import { useTheme } from "@mui/material/styles"; // To get current theme mode
// Import ALL step arrays that might be used
import {
  navbarTutorialSteps,
  dashboardTutorialSteps,
  chatboxTutorialSteps,
  todayPageTutorialSteps,
  profilePageTutorialSteps,
  pendingUpdatesPageTutorialSteps,
  agentsPageTutorialSteps,
} from "./tutorialSteps";

// Helper function to check if an element is visible (copied from tutorialSteps.js)
const isElementVisible = (selector) => {
  const element = document.querySelector(selector);
  if (!element) return false;
  let currentElement = element;
  while (currentElement && currentElement !== document.body) {
    if (window.getComputedStyle(currentElement).display === 'none') {
      return false;
    }
    currentElement = currentElement.parentElement;
  }
  return element.offsetParent !== null && window.getComputedStyle(element).visibility !== 'hidden';
};

const TutorialContext = createContext();

const LOCAL_STORAGE_COMPLETED_STEPS = "tutorialCompletedSteps";
const LOCAL_STORAGE_TUTORIAL_DISABLED = "tutorialDisabled";

const TutorialProvider = ({ children }) => {
  const [isTutorialActive, setIsTutorialActive] = useState(false); // Restored to false
  const [activeTutorialSteps, setActiveTutorialSteps] = useState([]);
  const [completedSteps, setCompletedSteps] = useState({});
  const [tutorialDisabled, setTutorialDisabled] = useState(true); // Changed initial state to true
  const [tourKey, setTourKey] = useState(0); // Kept for ShepherdRunner key prop
  const [currentTourOptionsForRunner, setCurrentTourOptionsForRunner] = useState(null); // State for options

  const muiTheme = useTheme();

  // Load progress from localStorage on mount
  useEffect(() => {
    const storedCompletedSteps = localStorage.getItem(
      LOCAL_STORAGE_COMPLETED_STEPS
    );
    if (storedCompletedSteps) {
      setCompletedSteps(JSON.parse(storedCompletedSteps));
    }
    const storedTutorialDisabled = localStorage.getItem(
      LOCAL_STORAGE_TUTORIAL_DISABLED
    );
    if (storedTutorialDisabled) {
      setTutorialDisabled(JSON.parse(storedTutorialDisabled));
    }

    // Firestore loading
    const uid = auth.currentUser?.uid;
    if (uid) {
      loadUserTutorialState(uid).then((firestoreState) => {
        if (firestoreState) {
          // Merge Firestore state with localStorage state
          // Firestore `tutorialDisabled` takes precedence
          setTutorialDisabled(
            firestoreState.tutorialDisabled !== undefined
              ? firestoreState.tutorialDisabled
              : JSON.parse(storedTutorialDisabled || "false")
          );
          // Merge completed steps: combine keys from both, Firestore can add to localStorage
          const localCompleted = storedCompletedSteps
            ? JSON.parse(storedCompletedSteps)
            : {};
          setCompletedSteps((prev) => ({
            ...localCompleted,
            ...(firestoreState.completedSteps || {}),
          }));
        }
      });
    }
  }, []);

  const completeStep = useCallback((stepId) => {
    setCompletedSteps((prev) => {
      const newCompletedSteps = { ...prev, [stepId]: true };
      localStorage.setItem(
        LOCAL_STORAGE_COMPLETED_STEPS,
        JSON.stringify(newCompletedSteps)
      );
      const uid = auth.currentUser?.uid;
      if (uid) {
        // Optional: Save all completed steps to Firestore on each step completion
        // saveUserTutorialState(uid, { completedSteps: newCompletedSteps });
      }
      return newCompletedSteps;
    });
  }, []);

  const markTutorialDisabled = useCallback(() => {
    // TODO: ESLint reported 'tourInstanceRef' not defined around lines 128-129.
    // Ensuring no tourInstanceRef logic remains here.
    //setIsTutorialActive(false) should handle unmounting ShepherdRunner, which then cleans up.
    setTutorialDisabled(true);
    setIsTutorialActive(false);
    setActiveTutorialSteps([]);
    setCurrentTourOptionsForRunner(null);
    localStorage.setItem(LOCAL_STORAGE_TUTORIAL_DISABLED, JSON.stringify(true));
    const uid = auth.currentUser?.uid;
    if (uid) {
      saveUserTutorialState(uid, { tutorialDisabled: true, completedSteps });
    }
  }, [completedSteps]); // Removed tourInstanceRef from deps

  const resetTutorialProgress = useCallback(() => {
    // TODO: ESLint reported 'tourInstanceRef' not defined around lines 128-129.
    // Ensuring no tourInstanceRef logic remains here.
    setCompletedSteps({});
    setTutorialDisabled(true); // Explicitly set to true
    setIsTutorialActive(false);
    setActiveTutorialSteps([]);
    setCurrentTourOptionsForRunner(null);
    localStorage.removeItem(LOCAL_STORAGE_COMPLETED_STEPS); // This can be kept or removed if completed steps are not relevant when disabled
    localStorage.setItem(LOCAL_STORAGE_TUTORIAL_DISABLED, JSON.stringify(true)); // Store true
    const uid = auth.currentUser?.uid;
    if (uid) {
      saveUserTutorialState(uid, {
        tutorialDisabled: true, // Save true to Firestore
        completedSteps: {}, // Clear completed steps on reset
      });
    }
  }, []);

  // const skipTutorial = useCallback(() => { // Removed duplicate
  //   setIsTutorialActive(false);
  //   // This is a session-only skip. No persistent state change.
  // }, []);

  const startTutorial = useCallback(
    (currentPathname, specificStepsArray = null) => {
      if (tutorialDisabled) {
        console.log("[TutorialContext] Tutorial is disabled, not starting.");
        return;
      }
      // Logic to determine stepsSource (as before)
      let stepsSource;
      if (specificStepsArray && Array.isArray(specificStepsArray)) {
        stepsSource = specificStepsArray;
      } else if (currentPathname) {
        if (currentPathname.includes("/agents")) {
          stepsSource = agentsPageTutorialSteps;
        } else if (currentPathname.includes("/profile/updates")) {
          stepsSource = pendingUpdatesPageTutorialSteps;
        } else if (currentPathname === "/profile") {
          stepsSource = profilePageTutorialSteps;
        } else if (currentPathname.includes("/today")) {
          stepsSource = todayPageTutorialSteps;
        } else if (currentPathname.includes("/dashboard")) {
          stepsSource = dashboardTutorialSteps;
        } else {
          stepsSource = navbarTutorialSteps;
        }
      } else {
        stepsSource = navbarTutorialSteps;
      }

      const allStepsForTheCurrentTour = initializeTutorial(muiTheme.palette.mode, stepsSource);
      const filteredSteps = allStepsForTheCurrentTour.filter(step => !completedSteps[step.id]);

      if (filteredSteps.length > 0) {
        const currentOptions = getTourOptions(muiTheme.palette.mode);
        setCurrentTourOptionsForRunner(currentOptions);
        setActiveTutorialSteps(filteredSteps);
        setIsTutorialActive(true);
        setTourKey(prevKey => prevKey + 1); // Increment key to trigger ShepherdRunner
        console.log(`[TutorialContext] Preparing ShepherdRunner. Steps count: ${filteredSteps.length}. Options ready: ${!!currentOptions}`);
      } else {
        setIsTutorialActive(false);
        setActiveTutorialSteps([]);
        setCurrentTourOptionsForRunner(null);
        if (!tutorialDisabled && allStepsForTheCurrentTour.length > 0) {
          console.log("All steps for this tutorial section have been completed.");
        }
      }
    },
    [muiTheme.palette.mode, completedSteps, tutorialDisabled]
  );

  const handleTourComplete = useCallback(() => {
    markTutorialDisabled(); // This also sets isTutorialActive = false & clears steps/options
  }, [markTutorialDisabled]);

  const handleTourCancel = useCallback(() => {
    setIsTutorialActive(false);
    setActiveTutorialSteps([]);
    setCurrentTourOptionsForRunner(null);
  }, []);

  const endTutorial = useCallback(() => {
    setIsTutorialActive(false);
    setActiveTutorialSteps([]);
    setCurrentTourOptionsForRunner(null);
  }, []);

  const skipTutorial = useCallback(() => {
    setIsTutorialActive(false);
    setActiveTutorialSteps([]);
    setCurrentTourOptionsForRunner(null);
  }, []);


  return (
    <TutorialContext.Provider
      value={{
        isTutorialActive,
        startTutorial,
        endTutorial, // Use this for a generic "close tour" button
        skipTutorial, // Specifically for "skip this tour sequence" button
        completeStep, // For marking individual steps as completed (e.g. for persistence)
        markTutorialDisabled, // For "don't show again"
        resetTutorialProgress,
        completedSteps,
        tutorialDisabled,
        activeTutorialSteps,
        // tourKey, // Not exposed, internal to trigger ShepherdRunner
        // currentTourOptionsForRunner, // Not exposed, internal
      }}
    >
      {children}
      {isTutorialActive && activeTutorialSteps.length > 0 && currentTourOptionsForRunner && (
        <ShepherdRunner
          key={tourKey}
          steps={activeTutorialSteps}
          tourOptions={currentTourOptionsForRunner}
          onComplete={handleTourComplete}
          onCancel={handleTourCancel}
        />
      )}
    </TutorialContext.Provider>
  );
};

export { TutorialContext, TutorialProvider };
